import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Sparkle, Robot, CheckCircle, XCircle, Lightbulb, Warning, Trophy, Target } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEFAULT_ENGINES = [
  { key: "chatgpt", label: "ChatGPT", color: "#10A37F" },
  { key: "claude", label: "Claude", color: "#D97706" },
  { key: "perplexity", label: "Perplexity", color: "#20808D" },
];

function parseJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch {}
  const m = raw.match(/\{[\s\S]*\}/);
  if (m) { try { return JSON.parse(m[0]); } catch {} }
  return null;
}

export default function ChatGPTVisibility() {
  const { activeBiz } = useOutletContext();
  const [data, setData] = useState({ latest: null, history: [], engines_available: [] });
  const [running, setRunning] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planRaw, setPlanRaw] = useState("");
  const [activeEngine, setActiveEngine] = useState("chatgpt");
  const [openPrompt, setOpenPrompt] = useState(null);
  const [competitors, setCompetitors] = useState(null);

  const engines = (data.engines_available && data.engines_available.length) ? data.engines_available : DEFAULT_ENGINES.map((e) => ({ ...e, key: e.key }));

  const reload = async () => {
    if (!activeBiz) return;
    try {
      const { data } = await api.get(`/businesses/${activeBiz.id}/ai-visibility`);
      setData(data);
    } catch (e) { /* ignore */ }
    try {
      const { data: comp } = await api.get(`/businesses/${activeBiz.id}/ai-visibility/competitors`);
      setCompetitors(comp);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeBiz]);

  const run = async () => {
    setRunning(true); setPlan(null); setPlanRaw("");
    try {
      const { data: pending } = await api.post(`/businesses/${activeBiz.id}/ai-visibility/run`, {});
      const runId = pending?.id;
      if (pending?.status === "done") {
        toast.success("AI search visibility check complete");
        reload();
        setRunning(false);
        return;
      }
      toast.info("Running across ChatGPT, Claude & Perplexity…");
      // Poll every 5s for up to 4 minutes
      const deadline = Date.now() + 240_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data: st } = await api.get(`/businesses/${activeBiz.id}/ai-visibility/runs/${runId}`);
          if (st?.status === "done") {
            toast.success("AI search visibility check complete across all engines");
            reload();
            setRunning(false);
            return;
          }
          if (st?.status === "failed") {
            toast.error(`Run failed: ${st?.error || "unknown error"}`);
            setRunning(false);
            return;
          }
        } catch { /* keep polling */ }
      }
      toast.error("Run is taking too long — check back in a minute");
    } catch (e) { toast.error(formatErr(e)); }
    setRunning(false);
  };

  const generatePlan = async () => {
    setPlanning(true);
    try {
      const { data } = await api.post(`/businesses/${activeBiz.id}/ai-visibility/improvement-plan`);
      setPlanRaw(data.raw);
      const p = parseJson(data.raw);
      if (p) setPlan(p);
      else toast.error("AI returned non-JSON, see raw output");
    } catch (e) { toast.error(formatErr(e)); }
    setPlanning(false);
  };

  const latest = data.latest;
  const enginesData = useMemo(() => (latest?.engines) || {}, [latest]);
  const activeEngineData = enginesData[activeEngine];

  // Ensure activeEngine is valid after first run
  useEffect(() => {
    if (latest && !enginesData[activeEngine]) {
      const firstKey = Object.keys(enginesData)[0];
      if (firstKey) setActiveEngine(firstKey);
    }
  }, [latest, enginesData, activeEngine]);

  if (!activeBiz) return <div className="p-12 text-zinc-500">Add a business first.</div>;

  return (
    <div className="p-6 lg:p-10 space-y-6 max-w-6xl">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tiny mb-1 flex items-center gap-2">
            <Robot size={14} weight="bold" /> AI Search Visibility · Multi-Engine
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="aivis-title">
            Are you cited in <span className="bg-[#10A37F] text-white px-2">ChatGPT</span>,{" "}
            <span className="bg-[#D97706] text-white px-2">Claude</span>, and{" "}
            <span className="bg-[#20808D] text-white px-2">Perplexity</span>?
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            We run the same 12 customer-intent prompts across 3 AI engines and measure which ones recommend {activeBiz.name}.
            This is the new Top-3 Map Pack — AI search now drives 700M+ weekly queries.
          </p>
        </div>
        <button className="btn-primary" onClick={run} disabled={running} data-testid="aivis-run">
          {running ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> Run Across All Engines</>}
        </button>
      </header>

      {!latest && !running && (
        <div className="bento p-12 text-center" data-testid="aivis-empty">
          <Robot size={32} className="mx-auto text-zinc-400 mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">No visibility checks yet</h3>
          <p className="text-zinc-600 max-w-md mx-auto">
            Click "Run Across All Engines" to ask ChatGPT, Claude, and Perplexity 12 customer-intent questions and see which AI engines recommend your business.
          </p>
        </div>
      )}

      {latest && (
        <>
          {/* Overall matrix — one card per engine */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="aivis-matrix">
            {DEFAULT_ENGINES.map((eng) => {
              const ed = enginesData[eng.key];
              const rate = ed?.mention_rate ?? null;
              const isActive = activeEngine === eng.key;
              return (
                <button
                  key={eng.key}
                  onClick={() => ed && setActiveEngine(eng.key)}
                  data-testid={`aivis-engine-card-${eng.key}`}
                  className={`bento p-5 text-left transition-all ${ed ? "cursor-pointer hover:-translate-y-0.5" : "opacity-40 cursor-not-allowed"} ${isActive ? "ring-2 ring-offset-2 ring-zinc-950" : ""}`}
                  style={{ borderLeft: `4px solid ${eng.color}` }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="label-tiny" style={{ color: eng.color }}>{eng.label}</span>
                    {ed?.mocked && (
                      <span title="Using mocked data — drop in API key to go live" className="inline-flex items-center gap-1 text-[10px] font-mono text-zinc-500">
                        <Warning size={10} weight="fill" /> MOCK
                      </span>
                    )}
                  </div>
                  <div className="font-display text-5xl font-black mt-2 tabular-nums">
                    {rate !== null ? rate : "—"}<span className="text-zinc-500 text-2xl">%</span>
                  </div>
                  {ed && (
                    <>
                      <div className="font-mono text-xs text-zinc-500 mt-1">
                        {ed.mentioned_count}/{ed.total} cited · avg #{ed.avg_position ?? "—"}
                      </div>
                      <div className="mt-3 h-1.5 bg-zinc-100">
                        <div className="h-full" style={{ width: `${rate || 0}%`, background: eng.color }}></div>
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          {/* Overall aggregate */}
          <div className="bento p-5 flex flex-wrap items-center justify-between gap-4 bg-zinc-950 text-white">
            <div>
              <div className="label-tiny text-zinc-400">Combined AI Visibility</div>
              <div className="font-display text-3xl font-black mt-1 tabular-nums" data-testid="aivis-overall">
                {latest.overall_mention_rate ?? latest.mention_rate}%
              </div>
            </div>
            <div className="text-xs text-zinc-400 font-mono">
              {latest.total_mentions ?? latest.mentioned_count} cites across {latest.total_prompts ?? latest.total} prompts · {new Date(latest.created_at).toLocaleString()}
            </div>
            <div>
              <Verdict rate={latest.overall_mention_rate ?? latest.mention_rate} />
            </div>
          </div>

          {/* Per-engine drill-down */}
          {activeEngineData && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-display text-2xl font-bold tracking-tight">
                  Prompts tested — <span style={{ color: DEFAULT_ENGINES.find(e => e.key === activeEngine)?.color }}>
                    {activeEngineData.label || activeEngine}
                  </span>
                </h2>
                <div className="flex gap-1">
                  {Object.keys(enginesData).map((k) => (
                    <button
                      key={k}
                      onClick={() => { setActiveEngine(k); setOpenPrompt(null); }}
                      data-testid={`aivis-tab-${k}`}
                      className={`px-3 py-1.5 text-xs font-mono font-bold border border-zinc-200 ${activeEngine === k ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                    >
                      {enginesData[k].label || k}
                    </button>
                  ))}
                </div>
              </div>
              {activeEngineData.results.map((r, i) => (
                <div key={i} className="bento" data-testid={`aivis-result-${i}`}>
                  <button
                    onClick={() => setOpenPrompt(openPrompt === i ? null : i)}
                    className="w-full p-5 text-left flex items-start justify-between gap-4 hover:bg-zinc-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        {r.mentioned ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#22C55E] text-white text-xs font-mono font-bold">
                            <CheckCircle size={12} weight="fill" /> CITED {r.position ? `#${r.position}` : ""}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#EF4444] text-white text-xs font-mono font-bold">
                            <XCircle size={12} weight="fill" /> NOT CITED
                          </span>
                        )}
                        <span className="label-tiny">{r.keyword}</span>
                        {r.citations && r.citations.length > 0 && (
                          <span className="text-[10px] font-mono text-zinc-500">{r.citations.length} citations</span>
                        )}
                      </div>
                      <p className="mt-2 text-sm font-medium">{r.prompt}</p>
                      {r.snippet && <p className="mt-2 text-xs font-mono text-zinc-600 italic">"{r.snippet}"</p>}
                    </div>
                    <span className="text-zinc-400 text-xs font-mono shrink-0">{openPrompt === i ? "−" : "+"}</span>
                  </button>
                  {openPrompt === i && (
                    <div className="border-t border-zinc-200 p-5 bg-zinc-50 space-y-3">
                      <div>
                        <div className="label-tiny mb-2">{activeEngineData.label || activeEngine}'s full answer</div>
                        <pre className="font-mono text-xs whitespace-pre-wrap leading-relaxed text-zinc-700">{r.answer || "(empty response)"}</pre>
                      </div>
                      {r.citations && r.citations.length > 0 && (
                        <div>
                          <div className="label-tiny mb-2">Citations</div>
                          <ul className="space-y-1">
                            {r.citations.map((c, ci) => (
                              <li key={ci} className="text-xs font-mono text-[#002FA7] break-all">
                                <a href={typeof c === "string" ? c : c?.url} target="_blank" rel="noreferrer" className="hover:underline">
                                  {typeof c === "string" ? c : (c?.title || c?.url)}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Who's beating you — competitor leaderboard across engines */}
          {competitors && competitors.combined && competitors.combined.length > 0 && (
            <div className="bento p-6 space-y-4 border-l-4 border-[#EF4444]">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                    <Trophy size={20} weight="fill" className="text-[#EF4444]" /> Who's Beating You
                  </h2>
                  <p className="text-zinc-600 text-sm mt-1">
                    Businesses the AI engines recommend instead of {activeBiz.name}. Target these names with
                    citation-building and brand-mention campaigns — when they show up in your AEO plan, you overtake them.
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="competitor-leaderboard">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left label-tiny">
                      <th className="py-2 pr-3">#</th>
                      <th className="py-2 pr-3">Competitor</th>
                      <th className="py-2 pr-3">Engines</th>
                      <th className="py-2 pr-3">Total Cites</th>
                      <th className="py-2 pr-3">Avg Pos</th>
                      <th className="py-2 pr-3">Keywords</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.combined.slice(0, 10).map((c, i) => (
                      <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`competitor-row-${i}`}>
                        <td className="py-2 pr-3 font-mono text-zinc-500 tabular-nums">{i + 1}</td>
                        <td className="py-2 pr-3 font-display font-bold">{c.name}</td>
                        <td className="py-2 pr-3">
                          <div className="flex gap-1">
                            {Object.keys(c.engines || {}).map((ek) => {
                              const eng = DEFAULT_ENGINES.find(e => e.key === ek);
                              if (!eng) return null;
                              const stat = c.engines[ek];
                              return (
                                <span
                                  key={ek}
                                  title={`${eng.label}: ${stat.cites}x, avg #${stat.avg_position ?? "—"}`}
                                  className="text-[10px] font-mono font-bold px-1.5 py-0.5 text-white"
                                  style={{ background: eng.color }}
                                >
                                  {eng.label[0]}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="py-2 pr-3 font-mono tabular-nums">{c.total_cites}</td>
                        <td className="py-2 pr-3 font-mono tabular-nums">
                          {c.avg_position_all ? `#${c.avg_position_all}` : "—"}
                        </td>
                        <td className="py-2 pr-3 text-xs text-zinc-500">
                          {(c.keywords || []).slice(0, 3).join(", ")}
                          {(c.keywords || []).length > 3 && ` +${c.keywords.length - 3}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2 pt-2 text-xs text-zinc-500 font-mono">
                <Target size={12} /> Legend: cross-engine dominance = coverage count. A competitor cited by all 3 engines is the highest-value target.
              </div>
            </div>
          )}

          <div className="bento p-6 space-y-4 border-l-4 border-[#FACC15]">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                  <Lightbulb size={20} weight="fill" className="text-[#FACC15]" /> AI Improvement Plan
                </h2>
                <p className="text-zinc-600 text-sm mt-1">Cross-engine tactics to get cited in ChatGPT, Claude, and Perplexity.</p>
              </div>
              <button className="btn-dark" onClick={generatePlan} disabled={planning} data-testid="aivis-plan-btn">
                {planning ? <span className="spinner"></span> : <><Sparkle size={14} weight="fill" /> Generate plan</>}
              </button>
            </div>
            {plan && (
              <div className="space-y-3">
                <p className="font-mono text-sm leading-relaxed">{plan.summary}</p>
                <div className="space-y-2">
                  {(plan.priorities || []).map((p, i) => (
                    <div key={i} className="border border-zinc-200 p-4" data-testid={`aivis-prio-${i}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <h4 className="font-display font-bold">{p.title}</h4>
                        <div className="flex items-center gap-2">
                          {(p.engines || []).map((ek) => {
                            const e = DEFAULT_ENGINES.find(x => x.key === ek);
                            return e ? (
                              <span key={ek} className="text-[10px] font-mono px-1.5 py-0.5 text-white" style={{ background: e.color }}>
                                {e.label}
                              </span>
                            ) : null;
                          })}
                          <span className={`label-tiny ${p.effort === "high" ? "text-[#EF4444]" : p.effort === "medium" ? "text-[#EAB308]" : "text-[#22C55E]"}`}>
                            {p.effort} effort
                          </span>
                        </div>
                      </div>
                      <p className="text-zinc-600 text-sm mt-1">{p.why}</p>
                      <div className="mt-2 p-3 bg-zinc-50 border-l-2 border-[#002FA7] text-sm font-mono">{p.action}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!plan && planRaw && <pre className="font-mono text-xs whitespace-pre-wrap p-4 bg-zinc-50">{planRaw}</pre>}
          </div>

          {data.history.length > 1 && (
            <div className="bento p-6">
              <h3 className="font-display text-lg font-bold mb-3">History — Combined AI Visibility</h3>
              <div className="flex items-end gap-1 h-24">
                {data.history.slice().reverse().map((h, i) => {
                  const v = h.overall_mention_rate ?? h.mention_rate ?? 0;
                  return (
                    <div key={i} className="flex-1 bg-zinc-900 hover:bg-zinc-700 transition-colors" style={{ height: `${v}%` }} title={`${v}% on ${new Date(h.created_at).toLocaleDateString()}`}></div>
                  );
                })}
              </div>
              <div className="font-mono text-xs text-zinc-500 mt-2">Overall mention rate over last {data.history.length} checks</div>
            </div>
          )}
        </>
      )}

      <div className="bento p-5 text-xs text-zinc-500 font-mono leading-relaxed">
        <strong className="text-zinc-700">How it works:</strong> We send the same 12 customer-intent prompts to each engine —
        ChatGPT (GPT-5.2), Claude (Sonnet 4.5), and Perplexity (Sonar Pro) — then parse whether your business is named.
        Perplexity runs live when <code>PERPLEXITY_API_KEY</code> is set; until then it returns deterministic demo data labeled <span className="text-[#EAB308]">MOCK</span>.
      </div>
    </div>
  );
}

function Verdict({ rate }) {
  let label, color;
  if (rate >= 60) { label = "Strong AI presence"; color = "text-[#22C55E]"; }
  else if (rate >= 30) { label = "Emerging — push harder"; color = "text-[#EAB308]"; }
  else { label = "Invisible to AI search"; color = "text-[#EF4444]"; }
  return <div className={`font-display text-2xl font-bold ${color}`}>{label}</div>;
}
